// # Filename: src/routes/AppRoutes.jsx
// ✅ Updated Code: key Game WS route by :id + location.search (rematch-safe)

import React from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";

import AppShell from "../layout/AppShell";
import ResponsiveLayout from "../layout/ResponsiveLayout";
import PublicAuthLayout from "../layout/PublicAuthLayout";

import RequireAuth from "./RequireAuth";
import { useUserContext } from "../context/userContext";

import HomePage from "../components/home/HomePage";
import LoginPage from "../components/user/LoginPage";
import SignupPage from "../components/user/RegisterPage";

import Lobby from "../components/lobby/components/Lobby";
import GamePage from "../components/game/Gamepage";
import AIGamePage from "../components/game/AIGamePage";
import RecruiterDemoPage from "../components/recruiter/RecruiterDemoPage";
import TechnicalPaper from "../components/technical-paper/TechnicalPaper";
import SudokuPage from "../components/sudoku/SudokuPage";
import ConnectFourAIPage from "../components/connect-four/ConnectFourAIPage";
import ConnectFourMPPage from "../components/connect-four/ConnectFourMPPage";
import CheckersAIPage from "../components/checkers/CheckersAIPage";
import CheckersMPPage from "../components/checkers/CheckersMPPage";
import PokerAIPage from "../components/poker/PokerAIPage";
import PokerMPPage from "../components/poker/PokerMPPage";
import PokerTournamentsPage from "../components/poker/PokerTournamentsPage";
import CreateTournamentPage from "../components/poker/CreateTournamentPage";
import LeaderboardPage from "../components/leaderboard/LeaderboardPage";


import { GameWebSocketProvider } from "../websocket/GameWebSocketProvider";

import { InviteProvider } from "../context/inviteContext";
import { NotificationProvider } from "../context/notificatonContext";
import { FriendsProvider } from "../context/friendsContext";
import { DirectMessageProvider } from "../context/directMessageContext";
import { LobbyProvider } from "../context/lobbyContext";


import { GameProvider } from "../context/gameContext";

function AuthedProviders({ children }) {
  const { authLoaded, isLoggedIn } = useUserContext();
  if (!authLoaded || !isLoggedIn) return children;

  return (
    <InviteProvider>
      <NotificationProvider>
        <FriendsProvider>
          <DirectMessageProvider>{children}</DirectMessageProvider>
        </FriendsProvider>
      </NotificationProvider>
    </InviteProvider>
  );
}

function ProtectedLayout() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  );
}

/**
 * ✅ Keyed route wrapper:
 * - Remounts GameWebSocketProvider when:
 *   - :id changes (new game)
 *   - query params change (new sessionKey/lobby)
 */
function GameRoute() {
  const { id } = useParams();
  const location = useLocation();

  // Step 1: Key on id + querystring so rematch navigation forces a clean remount
  const providerKey = `${id}:${location.search}`;

  return (
    <GameWebSocketProvider key={providerKey}>
      <GamePage />
    </GameWebSocketProvider>
  );
}

export default function AppRoutes() {
  const { authLoaded } = useUserContext();
  if (!authLoaded) return null;

  return (
    <LobbyProvider>
      <AppShell>
        <AuthedProviders>
          <Routes>
            <Route element={<ResponsiveLayout />}>
              <Route path="/" element={<HomePage />} />

              <Route element={<ProtectedLayout />}>
                {/* Lobby needs GameProvider */}
                <Route
                  path="/lobby/:gameType/:id"
                  element={
                    <GameProvider>
                      <Lobby />
                    </GameProvider>
                  }
                />

                {/* ✅ AI route must exist and must mount AIGamePage */}
                <Route path="/games/ai/:id" element={<AIGamePage />} />

                {/* ✅ Multiplayer WS route (keyed) */}
                <Route path="/games/:id" element={<GameRoute />} />

                <Route path="/games/sudoku" element={<SudokuPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/games/connect-four/ai" element={<ConnectFourAIPage />} />
                <Route path="/games/connect-four/:id" element={<ConnectFourMPPage />} />
                <Route path="/games/checkers/ai" element={<CheckersAIPage />} />
                <Route path="/games/checkers/ai/:id" element={<CheckersAIPage />} />
                <Route path="/games/checkers/:id" element={<CheckersMPPage />} />
                <Route path="/games/poker/ai" element={<PokerAIPage />} />
                <Route path="/games/poker/ai/:id" element={<PokerAIPage />} />
                <Route path="/games/poker/:id" element={<PokerMPPage />} />
                <Route path="/tournaments" element={<PokerTournamentsPage />} />
                <Route path="/tournaments/create" element={<CreateTournamentPage />} />
                <Route path="/tournaments/edit/:type/:id" element={<CreateTournamentPage />} />
                <Route path="/recruiter-demo" element={<RecruiterDemoPage />} />
                <Route path="/technical-paper" element={<TechnicalPaper />} />
              </Route>
            </Route>

            <Route element={<PublicAuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/register" element={<SignupPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthedProviders>
      </AppShell>
    </LobbyProvider>
  );
}
